import { Component, OnInit, AfterViewInit, ViewChild, ViewChildren, QueryList, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MobileStreamService } from 'src/app/Services/mobile-stream.service';
import { ScheduleListService } from 'src/app/Services/schedule-list.service';
import { ScheduleCreateService } from 'src/app/Services/schedule-create.service';
import { ParameterService } from 'src/app/Services/parameter.service';
import { ProjectService } from 'src/app/Services/project.service';
import { ExecutionContainerComponent } from 'src/app/AppBaseComponents/execution-container/execution-container.component';
import { NotifyService } from 'src/app/Services/notify.service';
import { isApiErrorResponse } from 'src/app/Services/api-response.util';
import { BASE_RESOLUTIONS } from 'src/app/Constant/resolution.constants';
import { DELAY_LIST } from 'src/app/Constant/delay.constants';
import * as moment from 'moment-timezone';
import { WizardSuite } from 'src/app/Inteface/wizard-suite';
import { WizardStep } from 'src/app/Inteface/wizard-step';

@Component({
  selector: 'ze-create-schedule',
  templateUrl: './create-schedule.component.html',
  styleUrls: ['./create-schedule.component.scss']
})
export class CreateScheduleComponent implements OnInit, AfterViewInit {

  @ViewChild('masterExecutionContainer', { static: false })
  masterExecutionContainer!: ExecutionContainerComponent;

  @ViewChildren('suiteExecContainer')
  suiteExecutionContainers!: QueryList<ExecutionContainerComponent>;

  // When true, master execution settings provide a CSV; hide upload in per-suite containers
  masterHasCsv = false;

  readonly steps: WizardStep[] = [
    { id: 'suites', title: 'Test suites', subtitle: 'Choose what to run' },
    { id: 'occurrences', title: 'Number of Runs', subtitle: 'Executions per trigger' },
    { id: 'execution', title: 'Execution config', subtitle: 'Environment and notifications' },
    { id: 'configure', title: 'Configure', subtitle: 'Date, time, and recurrence' }
  ];

  currentStepIndex = 0;

  suites: WizardSuite[] = [];
  manualSuiteOrder: WizardSuite[] = [];
  suiteFilterText = '';
  sortOption: 'CUSTOM' | 'NAME_ASC' | 'NAME_DESC' = 'CUSTOM';
  sortMenuOpen = false;
  sortOptions = [
    { value: 'CUSTOM' as const, label: 'Custom order' },
    { value: 'NAME_ASC' as const, label: 'Name (A → Z)' },
    { value: 'NAME_DESC' as const, label: 'Name (Z → A)' }
  ];
  expandedSuiteId: string | null = null;
  suiteOverrides: Map<string, boolean> = new Map();
  suiteExecutionContainerRefs: Map<string, ExecutionContainerComponent> = new Map();
  suiteExecutionTypes: Map<string, { web: boolean; mobile: boolean }> = new Map();

  scheduleName = '';
  description = '';

  /** Step 2 — configure schedule (defaults match UI reference). */
  startDate = '';
  startTime = '';
  startTimeText = '';
  endDate = '';
  endAfterCount = 10;
  startDateMin = '';

  @ViewChild('startTimeContainer', { static: false })
  startTimeContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('timezoneControl', { static: false })
  timezoneControl!: ElementRef<HTMLDivElement>;
  @ViewChild('hourInput', { static: false })
  hourInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('minuteInput', { static: false })
  minuteInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('timezoneInput', { static: false })
  timezoneInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('hourControl', { static: false })
  hourControlRef!: ElementRef<HTMLDivElement>;
  @ViewChild('minuteControl', { static: false })
  minuteControlRef!: ElementRef<HTMLDivElement>;
  readonly maxScheduleYear = 2099;
  readonly maxScheduleDate = '2099-12-31';
  startDateMax = this.maxScheduleDate;
  repeat: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'WORK WEEK' | 'WEEKDAYS' | 'NEVER' = 'DAILY';
  frequencyEvery = 1;
  endCondition: 'NEVER' | 'TILL DATE' | 'AFTER' = 'NEVER';

  // Weekly days (0=Sun, 1=Mon, ..., 6=Sat)
  selectedWeeklyDays: number[] = [];
  // Work week days selector
  selectedWorkWeekDays: number[] = [1, 2, 3, 4, 5]; // Mon-Fri
  // Backfill: if schedule start time is in the past, trigger immediately upon creation/edit
  backfillAllowed = false;
  // Start time values stored for backend in 24-hour HH:mm format
  startTimeHour = '12';
  startTimeMinute = '00';
  startTimeAmPm: 'AM' | 'PM' = 'AM';
  hourOptions: string[] = Array.from({ length: 12 }, (_v, i) => String(i + 1).padStart(2, '0'));
  minuteOptions: string[] = Array.from({ length: 60 }, (_v, i) => String(i).padStart(2, '0'));
  amPmOptions: string[] = ['AM', 'PM'];
  
  // Dropdown state
  openDropdowns: { hour: boolean; minute: boolean; ampm: boolean } = { hour: false, minute: false, ampm: false };
  filteredHours: string[] = [];
  filteredMinutes: string[] = [];
  timezoneDropdownOpen = false;
  timezoneSearch = '';
  highlightedTimezone = '';
  highlightedHour = '';
  highlightedMinute = '';

  // Monthly options
  monthlyMode: 'fixed' | 'ordinal' = 'fixed';
  monthlyDay = 1;
  monthlyOrdinalWeek = 1; // 1st, 2nd, 3rd, 4th, 5th
  monthlyOrdinalDayOfWeek = 1; // Monday=1, Tuesday=2, etc.

  // Yearly options
  yearlyMonth = 1;
  yearlyDay = 1;
  yearlyDate = '';

  readonly timezoneOptions = moment
    .tz
    .names()
    .map(tz => {
      const offset = moment.tz(tz).utcOffset();
      const sign = offset >= 0 ? '+' : '-';
      const abs = Math.abs(offset);
      const hours = String(Math.floor(abs / 60)).padStart(2, '0');
      const minutes = String(abs % 60).padStart(2, '0');
      return `(GMT${sign}${hours}:${minutes}) ${tz}`;
    })
    .sort((a, b) => {
      const aTz = a.replace(/^\(GMT.*\)\s*/, '');
      const bTz = b.replace(/^\(GMT.*\)\s*/, '');
      const aOffset = moment.tz(aTz).utcOffset();
      const bOffset = moment.tz(bTz).utcOffset();
      if (aOffset !== bOffset) {
        return aOffset - bOffset;
      }
      return a.localeCompare(b);
    });

  timezone = '(GMT+05:30) Asia/Kolkata';
  timezoneInputValue = '(GMT+05:30) Asia/Kolkata';

  get filteredTimezoneOptions(): string[] {
    const filter = (this.timezoneSearch || '').trim().toLowerCase();
    if (!filter) {
      return this.timezoneOptions;
    }
    return this.timezoneOptions.filter(option => option.toLowerCase().includes(filter));
  }

  readonly repeatOptions = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY', 'WORK WEEK', 'WEEKDAYS', 'NEVER'];
  readonly endConditionOptions = [
    { value: 'NEVER' as const, label: 'NEVER' },
    { value: 'TILL DATE' as const, label: 'ON' },
    { value: 'AFTER' as const, label: 'AFTER' }
  ];
  readonly daysOfWeek = [
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 },
    { label: 'Sunday', value: 0 }
  ];
  readonly ordinalWeeks = [
    { label: '1st', value: 1 },
    { label: '2nd', value: 2 },
    { label: '3rd', value: 3 },
    { label: '4th', value: 4 },
    { label: '5th', value: 5 }
  ];

  /** Step 3 — execution occurrences per trigger. */
  executionsPerTrigger = 1;
  /** Seconds to wait between one suite execution and the next; applies to SEQUENTIAL mode only. */
  delayBetweenExecutions = 0;
  /** PARALLEL (default) or SEQUENTIAL — suites of a trigger run one after another on the same Helios. */
  executionMode: 'PARALLEL' | 'SEQUENTIAL' = 'PARALLEL';
  /** SEQUENTIAL only: keep running the remaining suites when one fails or is aborted. */
  continueOnFailure = true;
  maxParallelCount = 1;
  retryOnFailure = false;
  maxRetries = 1;

  readonly executionModeOptions = [
    { label: 'Parallel', value: 'PARALLEL' },
    { label: 'Sequential', value: 'SEQUENTIAL' }
  ];
  readonly sequentialDelayOptions = [
    { label: 'No delay', value: 0 },
    { label: '30 seconds', value: 30 },
    { label: '1 minute', value: 60 },
    { label: '5 minutes', value: 300 },
    { label: '10 minutes', value: 600 },
    { label: 'Custom', value: -1 }
  ];
  readonly maxSequentialDelaySec = 600; // 10 minutes
  /** Value of the delay select: a preset in seconds, or -1 when a custom delay is entered. */
  delayPreset = 0;

  onDelayPresetChange(): void {
    if (Number(this.delayPreset) !== -1) {
      this.delayBetweenExecutions = Number(this.delayPreset);
    }
  }

  private syncDelayPreset(): void {
    const isPreset = this.sequentialDelayOptions.some(o => o.value !== -1 && o.value === this.delayBetweenExecutions);
    this.delayPreset = isPreset ? this.delayBetweenExecutions : -1;
  }

  get isCustomDelay(): boolean {
    return Number(this.delayPreset) === -1;
  }

  get isSequentialDelayValid(): boolean {
    const d = Number(this.delayBetweenExecutions);
    return !this.isSequentialMode || (Number.isInteger(d) && d >= 0 && d <= this.maxSequentialDelaySec);
  }

  /** SEQUENTIAL: suite ids in run order. Suites selected later are appended at the end. */
  sequenceIds: string[] = [];

  /** Selected suites in run order (see sequenceIds). */
  get sequencedSuites(): WizardSuite[] {
    const rank = (id: string): number => {
      const i = this.sequenceIds.indexOf(id);
      return i < 0 ? Number.MAX_SAFE_INTEGER : i;
    };
    return this.suites
      .filter(s => s.selected)
      .map((suite, index) => ({ suite, index }))
      .sort((a, b) => rank(a.suite.id) - rank(b.suite.id) || a.index - b.index)
      .map(x => x.suite);
  }

  dropSequence(event: CdkDragDrop<WizardSuite[]>): void {
    const ordered = this.sequencedSuites;
    moveItemInArray(ordered, event.previousIndex, event.currentIndex);
    this.sequenceIds = ordered.map(s => s.id);
  }

  moveSequence(index: number, delta: number): void {
    const ordered = this.sequencedSuites;
    const target = index + delta;
    if (target < 0 || target >= ordered.length) {
      return;
    }
    moveItemInArray(ordered, index, target);
    this.sequenceIds = ordered.map(s => s.id);
  }

  get isSequentialMode(): boolean {
    return this.executionMode === 'SEQUENTIAL';
  }

  /** Step 4 — master execution configuration. */
  executionTypeArray: string[] = ['WEB'];
  execHub = 'Xpeer Cloud';
  execPort = 'Default Port';
  execBrowser: string[] = ['Chrome'];
  execScreenResolution = 'default_resolution';
  execCustomResolutionX = 1920;
  execCustomResolutionY = 1080;
  execDelaySeconds = '0';
  execImplicitDelay = false;

  optRunOnXpeerHub = true;
  optRunOnXpeerMobileHub = false;
  optNotifyOnCompletion = false;
  optVisualComparison = false;

  // Mobile execution
  execMobileDevice = '';
  execApkFileName = '';
  execApkFile?: File;

  isLoadingMobileDevices = false;
  mobileDeviceLoadError = '';
  mobileDeviceOptions: string[] = [];

  editScheduleId: string | null = null;
  private originalPreviousRunTime: number | null = null;
  private originalTriggeredCount: number | null = null;
  isLoadingSchedule = false;
  isSubmitting = false;
  scheduleLoadError = '';
  private pendingScheduleData: any = null;
  private pendingMasterConfig: any = null;
   /** Per-suite exec_config from getScheduleById, applied when suite containers mount. */
  private pendingPerSuiteConfigs: Map<string, any> = new Map();
  /** Preserved attached_files from schedule load for edit submit. */
  private suiteAttachedFilesFromSchedule: Map<string, any[]> = new Map();
  /** Suite ids whose exec_config was already applied to the execution container. */
  private appliedSuiteConfigIds: Set<string> = new Set();
  /** Baseline CSV/APK UI state per suite when edit form is hydrated (for post-update uploads). */
  private suiteFileSnapshots: Map<string, {
    useDefaultCsv: boolean;
    useDefaultApk: boolean;
    hadScheduleCsv: boolean;
    hadScheduleApk: boolean;
    setCsvForSuite: boolean;
    setApkForSuite: boolean;
  }> = new Map();
  suiteUploadedCsvs: Map<string, File | null> = new Map();
  suiteUploadedApks: Map<string, File | null> = new Map();
  suiteUseDefaultCsvs: Map<string, boolean> = new Map();
  suiteUseDefaultApks: Map<string, boolean> = new Map();
  suiteSetCsvForSuites: Map<string, boolean> = new Map();
  suiteSetApkForSuites: Map<string, boolean> = new Map();
  suiteCsvResetRequesteds: Map<string, boolean> = new Map();
  suiteApkResetRequesteds: Map<string, boolean> = new Map();
  private suitesLoaded = false;

  readonly hubOptions = ['Xpeer Cloud', 'Private Hub', 'Local'];
  readonly portOptions = ['Default Port', '443', '4444', '8080'];
  readonly browserOptions = ['Chrome', 'Firefox', 'Edge', 'Safari'];
  readonly resolutionOptions = BASE_RESOLUTIONS;
  readonly webDelayOptions = DELAY_LIST;

  constructor(
    private http: HttpClient,
    private mobileStreamService: MobileStreamService,
    private route: ActivatedRoute,
    private router: Router,
    private scheduleListService: ScheduleListService,
    private scheduleCreateService: ScheduleCreateService,
    private notifyService: NotifyService,
    private parameterService: ParameterService,
    private projectService: ProjectService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.scheduleName = '';
    this.route.paramMap.subscribe(params => {
      const scheduleId = params.get('scheduleId');
      if (scheduleId) {
        this.editScheduleId = scheduleId;
        this.loadScheduleForEdit(scheduleId);
      }
    });
    this.loadSuites();
    // initialize schedule dates/times to current local values
    const today = this.getTodayDateString();
    this.startDateMin = today;
    this.startDate = today;
    this.startTime = this.getCurrentTimeString();
    this.syncStartTimeToInputs(this.startTime);
    this.endDate = today;
    this.yearlyDate = today;
  }

  private getTodayDateString(): string {
    const d = new Date();
    const tzoffset = d.getTimezoneOffset() * 60000; // offset in ms
    const localISO = new Date(d.getTime() - tzoffset).toISOString().slice(0, 10);
    return localISO;
  }

  private getAdjustedStartDateTime(
    year: number,
    month: number,
    day: number
  ): {
    startDateTimestamp: number;
    startTime: string;
  } {

    const selectedDate = new Date(year, month - 1, day);

    return {
      startDateTimestamp: selectedDate.getTime(),
      startTime: this.startTime
    };
  }

  private getCurrentTimeString(): string {
    const d = new Date();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  syncStartTimeToInputs(time: string): void {
    if (!time) {
      return;
    }
    const [hh = '00', mm = '00'] = time.split(':');
    let hour = Number(hh);
    const minute = Number(mm);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    if (hour === 0) {
      hour = 12;
    } else if (hour > 12) {
      hour -= 12;
    }
    this.startTimeHour = String(hour).padStart(2, '0');
    this.startTimeMinute = String(minute).padStart(2, '0');
    this.startTimeAmPm = ampm;
    this.startTimeText = `${this.startTimeHour}:${this.startTimeMinute} ${ampm}`;
    this.startTime = `${String(Number(hh)).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }

  selectAmPm(value: 'AM' | 'PM'): void {
    this.startTimeAmPm = value;
    this.closeDropdown('ampm');
    this.onStartTimePartChange();
  }

  onAmPmKeyDown(event: KeyboardEvent): void {
    if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      this.startTimeAmPm = this.startTimeAmPm === 'AM' ? 'PM' : 'AM';
      this.onStartTimePartChange();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleDropdown('ampm');
    } else if (event.key === 'Escape') {
      this.closeDropdown('ampm');
    }
  }

  onStartTimePartChange(): void {
    // For input fields with datalist, validate and normalize the values
    const hour = String(this.startTimeHour || '').trim();
    const minute = String(this.startTimeMinute || '').trim();
    const ampm = String(this.startTimeAmPm || '').trim();

    // Validate and normalize hour (1-12)
    let validHour = hour;
    if (hour) {
      const hourNum = parseInt(hour, 10);
      if (!isNaN(hourNum)) {
        if (hourNum < 1) {
          validHour = '01';
        } else if (hourNum > 12) {
          validHour = '12';
        } else {
          validHour = String(hourNum).padStart(2, '0');
        }
      } else {
        validHour = '';
      }
    }
    this.startTimeHour = validHour;

    // Validate and normalize minute (00-59)
    let validMinute = minute;
    if (minute) {
      const minNum = parseInt(minute, 10);
      if (!isNaN(minNum)) {
        if (minNum > 59) {
          validMinute = '59';
        } else {
          validMinute = String(minNum).padStart(2, '0');
        }
      } else {
        validMinute = '';
      }
    }
    this.startTimeMinute = validMinute;

    // AM/PM is a select dropdown - ensure it's valid
    if (ampm !== 'AM' && ampm !== 'PM') {
      this.startTimeAmPm = 'AM';
    }

    // Update display text and internal 24-hour format only if all parts are valid
    if (this.startTimeHour && this.startTimeMinute && this.startTimeAmPm) {
      this.startTimeText = `${this.startTimeHour}:${this.startTimeMinute} ${this.startTimeAmPm}`;
      const parsed = this.parseTimeText(this.startTimeText);
      if (parsed) {
        this.startTime = parsed.startTime;
      } else {
        this.startTime = '';
      }
    } else {
      this.startTime = '';
      this.startTimeText = '';
    }
  }

  openDropdown(type: 'hour' | 'minute' | 'ampm'): void {
    this.closeAllDropdowns();
    this.openDropdowns[type] = true;
    if (type === 'hour') {
      this.filteredHours = this.hourOptions;
       this.highlightedHour = this.startTimeHour;
      this.cdr.detectChanges();
      this.scrollTimeOptionIntoView('hour', this.hourOptions.indexOf(this.startTimeHour));
    } else if (type === 'minute') {
      this.filteredMinutes = this.minuteOptions;
      this.highlightedMinute = this.startTimeMinute;
      this.cdr.detectChanges();
      this.scrollTimeOptionIntoView('minute', this.minuteOptions.indexOf(this.startTimeMinute));
    }
  }

  closeDropdown(type: 'hour' | 'minute' | 'ampm'): void {
    this.openDropdowns[type] = false;
    if (type === 'hour') this.highlightedHour = '';
    if (type === 'minute') this.highlightedMinute = '';
  }

  closeAllDropdowns(): void {
    this.openDropdowns = { hour: false, minute: false, ampm: false };
    this.highlightedHour = '';
    this.highlightedMinute = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    const startTimeContains = this.startTimeContainer && this.startTimeContainer.nativeElement && this.startTimeContainer.nativeElement.contains(target);
    const timezoneContains = this.timezoneControl && this.timezoneControl.nativeElement && this.timezoneControl.nativeElement.contains(target);

    if (!startTimeContains) {
      this.closeAllDropdowns();
    }

    if (!timezoneContains) {
      this.closeTimezoneDropdown();
    }
  }

  toggleDropdown(type: 'hour' | 'minute' | 'ampm'): void {
    if (this.openDropdowns[type]) {
      this.closeDropdown(type);
    } else {
      this.openDropdown(type);
    }
  }

  openTimezoneDropdown(): void {
     if (this.timezoneDropdownOpen) return;
    this.timezoneSearch = '';
    this.timezoneInputValue = '';
    this.timezoneDropdownOpen = true;
    this.highlightedTimezone = '';
    // Wait for *ngIf to render the dropdown, then highlight and scroll
    setTimeout(() => {
      this.highlightedTimezone = this.timezone;
      this.cdr.detectChanges();
      this.scrollTimezoneOptionIntoView(this.filteredTimezoneOptions.indexOf(this.timezone));
    }, 0);
  }

  closeTimezoneDropdown(): void {
    this.timezoneSearch = '';
    this.timezoneInputValue = this.timezone;
    this.highlightedTimezone = '';
    this.timezoneDropdownOpen = false;
  }

  toggleTimezoneDropdown(): void {
    if (this.timezoneDropdownOpen) {
      this.closeTimezoneDropdown();
    } else {
      this.openTimezoneDropdown();
    }
  }

  selectTimezone(value: string): void {
    this.timezone = value;
    this.timezoneInputValue = value;
    this.timezoneSearch = '';
    this.highlightedTimezone = '';
    this.timezoneDropdownOpen = false;
  }

  onTimezoneSearch(value: string): void {
    if (this.timezoneDropdownOpen) {
      this.timezoneSearch = value;
      this.highlightedTimezone = '';
    }
  }

  onTimezoneKeyDown(event: KeyboardEvent): void {
    if (!this.timezoneDropdownOpen) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        event.preventDefault();
        this.openTimezoneDropdown();
      }
      return;
    }
    const options = this.filteredTimezoneOptions;
    const curIdx = options.indexOf(this.highlightedTimezone);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = options[Math.min(curIdx + 1, options.length - 1)];
      this.highlightedTimezone = next;
      this.setTimezoneDisplay(next);
      this.cdr.detectChanges();
      this.scrollTimezoneOptionIntoView(options.indexOf(next));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prev = options[Math.max(curIdx - 1, 0)];
      this.highlightedTimezone = prev;
      this.setTimezoneDisplay(prev);
      this.cdr.detectChanges();
      this.scrollTimezoneOptionIntoView(options.indexOf(prev));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.highlightedTimezone) {
        this.selectTimezone(this.highlightedTimezone);
      } else if (options.length === 1) {
        this.selectTimezone(options[0]);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.closeTimezoneDropdown();
    }
  }

  private setTimezoneDisplay(value: string): void {
    if (this.timezoneInputRef) {
      this.timezoneInputRef.nativeElement.value = value;
    }
  }

  private scrollTimezoneOptionIntoView(index: number): void {
    setTimeout(() => {
      const container = document.querySelector('.timezone-dropdown .time-picker-options');
      if (container) {
        const items = container.querySelectorAll('.time-picker-option');
        if (items[index]) {
          (items[index] as HTMLElement).scrollIntoView({ block: 'nearest' });
        }
      }
    });
  }

  onTimezoneBlur(): void {
    setTimeout(() => this.closeTimezoneDropdown(), 200);
  }

  onTimeInputChange(type: 'hour' | 'minute', event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');

    if (value.length > 2) {
      value = value.slice(0, 2);
    }

    if (type === 'hour') {
      this.startTimeHour = value;
      this.filteredHours = value ? this.hourOptions.filter(h => h.startsWith(value) || h.includes(value)) : this.hourOptions;
    } else if (type === 'minute') {
      this.startTimeMinute = value;
      this.filteredMinutes = value ? this.minuteOptions.filter(m => m.startsWith(value) || m.includes(value)) : this.minuteOptions;
    }
  }

  validateTimeInput(type: 'hour' | 'minute'): void {
    const value = String(type === 'hour' ? this.startTimeHour : this.startTimeMinute || '').trim();
    if (!value) {
      if (type === 'hour') {
        this.startTimeHour = '';
      } else {
        this.startTimeMinute = '';
      }
      this.onStartTimePartChange();
      return;
    }

    const numeric = parseInt(value, 10);
    if (isNaN(numeric)) {
      if (type === 'hour') {
        this.startTimeHour = '';
      } else {
        this.startTimeMinute = '';
      }
      this.onStartTimePartChange();
      return;
    }

    if (type === 'hour') {
      const normalized = Math.min(Math.max(numeric, 1), 12);
      this.startTimeHour = String(normalized).padStart(2, '0');
    } else {
      const normalized = Math.min(Math.max(numeric, 0), 59);
      this.startTimeMinute = String(normalized).padStart(2, '0');
    }

    this.onStartTimePartChange();
  }

   onTimeInputKeyDown(type: 'hour' | 'minute', event: KeyboardEvent): void {
    const key = event.key;
    const isDigit = /^\d$/.test(key);
    const isNavigationKey = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Escape', 'Home', 'End'].includes(key);

    if (!isDigit && !isNavigationKey) {
      event.preventDefault();
      return;
    }

    const isOpen = this.openDropdowns[type];
    const options = type === 'hour' ? this.filteredHours : this.filteredMinutes;
    const getCurrent = () => type === 'hour' ? this.highlightedHour : this.highlightedMinute;
    const setValue = (v: string) => { if (type === 'hour') this.highlightedHour = v; else this.highlightedMinute = v; };

    if (key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) {
        this.openDropdown(type);
      } else {
        const curIdx = options.indexOf(getCurrent());
        const next = options[Math.min(curIdx + 1, options.length - 1)];
        setValue(next);
        this.updateTimeInputLive(type, next);
        this.cdr.detectChanges();
        this.scrollTimeOptionIntoView(type, options.indexOf(next));
      }
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      if (isOpen) {
        const curIdx = options.indexOf(getCurrent());
        const prev = options[Math.max(curIdx - 1, 0)];
        setValue(prev);
        this.updateTimeInputLive(type, prev);
        this.cdr.detectChanges();
        this.scrollTimeOptionIntoView(type, options.indexOf(prev));
      }
    } else if (key === 'Enter') {
      event.preventDefault();
      if (isOpen) {
        const cur = getCurrent();
        if (cur) {
          this.selectTime(type, cur);
        } else if (options.length === 1) {
          this.selectTime(type, options[0]);
        }
      } else {
        this.openDropdown(type);
      }
    } else if (key === 'Escape') {
      event.preventDefault();
      this.closeDropdown(type);
    }
  }

  private updateTimeInputLive(type: 'hour' | 'minute', value: string): void {
    if (type === 'hour') {
      this.startTimeHour = value;
    } else {
      this.startTimeMinute = value;
    }
  }

  private scrollTimeOptionIntoView(type: 'hour' | 'minute', index: number): void {
    setTimeout(() => {
      const controlRef = type === 'hour' ? this.hourControlRef : this.minuteControlRef;
      if (!controlRef) return;
      const container = controlRef.nativeElement.querySelector('.time-picker-options');
      if (container) {
        const items = container.querySelectorAll('.time-picker-option');
        if (items[index]) {
          (items[index] as HTMLElement).scrollIntoView({ block: 'nearest' });
        }
      }
    });
  }

  selectTime(type: 'hour' | 'minute', value: string): void {
    if (type === 'hour') {
      this.startTimeHour = value;
      this.closeDropdown('hour');
    } else if (type === 'minute') {
      this.startTimeMinute = value;
      this.closeDropdown('minute');
    }
    this.onStartTimePartChange();
  }

  updateStartTimeFromInputs(): void {
    const parsed = this.parseTimeText(this.startTimeText);
    if (!parsed) {
      this.startTime = '';
      return;
    }
    this.startTime = parsed.startTime;
    this.startTimeHour = parsed.hour;
    this.startTimeMinute = parsed.minute;
    this.startTimeAmPm = parsed.amPm;
  }



  private parseTimeText(value: string): { startTime: string; hour: string; minute: string; amPm: 'AM' | 'PM' } | null {
    const text = String(value).trim().toUpperCase();
    const twelveHour = text.match(/^(0?[1-9]|1[0-2]):([0-5]\d)\s*(AM|PM)$/i);
    if (twelveHour) {
      let hour = Number(twelveHour[1]);
      const minute = twelveHour[2];
      const amPm = (twelveHour[3] || 'AM').toUpperCase() as 'AM' | 'PM';
      if (amPm === 'PM' && hour !== 12) {
        hour += 12;
      }
      if (amPm === 'AM' && hour === 12) {
        hour = 0;
      }
      return {
        startTime: `${String(hour).padStart(2, '0')}:${minute}`,
        hour: String(Number(twelveHour[1])).padStart(2, '0'),
        minute,
        amPm
      };
    }
    const twentyFourHour = text.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
    if (twentyFourHour) {
      const hour24 = Number(twentyFourHour[1]);
      const minute = twentyFourHour[2];
      const amPm = hour24 >= 12 ? 'PM' : 'AM';
      let hour12 = hour24 % 12;
      if (hour12 === 0) {
        hour12 = 12;
      }
      return {
        startTime: `${String(hour24).padStart(2, '0')}:${minute}`,
        hour: String(hour12).padStart(2, '0'),
        minute,
        amPm
      };
    }
    return null;
  }

  private parseLocalDate(dateStr: string): Date | null {
    if (!dateStr) {
      return null;
    }
    const parts = dateStr.split('-');
    if (parts.length !== 3) {
      return null;
    }
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  private parseStartDateTime(): Date | null {
    if (!this.startDate || !this.startTime) {
      return null;
    }
    const [hour, minute] = this.startTime.split(':').map(Number);
    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return null;
    }
    const date = this.parseLocalDate(this.startDate);
    if (!date) {
      return null;
    }
    date.setHours(hour, minute, 0, 0);
    return date;
  }

  private to24Hour(hour12: string, amPm: 'AM' | 'PM'): number {
    const hour = Number(hour12);
    if (amPm === 'AM' && hour === 12) {
      return 0;
    }
    if (amPm === 'PM' && hour !== 12) {
      return hour + 12;
    }
    return hour;
  }

  private parseIsoDateString(dateStr: string): Date | null {
    const date = this.parseLocalDate(dateStr);
    return date && !Number.isNaN(date.getTime()) ? date : null;
  }

  isDateWithinAllowedRange(dateStr: string): boolean {
    const parsed = this.parseIsoDateString(dateStr);
    if (!parsed) {
      return false;
    }
    return parsed.getFullYear() <= this.maxScheduleYear;
  }

  private isTodaySelected(): boolean {
    return this.startDate === this.getTodayDateString();
  }

  isHourOptionDisabled(hour: string, amPm: 'AM' | 'PM'): boolean {
    if (!this.isTodaySelected()) {
      return false;
    }
    const optionHour24 = this.to24Hour(hour, amPm);
    const now = new Date();
    return optionHour24 < now.getHours();
  }

  isMinuteOptionDisabled(minute: string): boolean {
    if (!this.isTodaySelected()) {
      return false;
    }
    const selectedHour24 = this.to24Hour(this.startTimeHour, this.startTimeAmPm);
    const now = new Date();
    if (selectedHour24 !== now.getHours()) {
      return false;
    }
    return Number(minute) < now.getMinutes();
  }

  isAmPmOptionDisabled(option: 'AM' | 'PM'): boolean {
    if (!this.isTodaySelected()) {
      return false;
    }
    const now = new Date();
    return now.getHours() >= 12 && option === 'AM';
  }

  ngAfterViewInit(): void {
    this.applyMasterConfigToExecutionContainer();
    // Register all currently visible suite execution containers
    this.updateSuiteContainerReferences();
    // Subscribe to changes in the suite containers QueryList
    if (this.suiteExecutionContainers) {
      this.suiteExecutionContainers.changes.subscribe(() => {
        this.updateSuiteContainerReferences();
      });
    }
  }

  private applyPendingPerSuiteConfigs(): void {
    if (!this.pendingPerSuiteConfigs.size) {
      return;
    }

    this.pendingPerSuiteConfigs.forEach((execConfig, suiteId) => {
      if (!this.suiteOverrides.get(suiteId)) {
        return;
      }

      const container = this.suiteExecutionContainerRefs.get(suiteId);
      if (!container || !execConfig) {
        return;
      }

      if (!this.appliedSuiteConfigIds.has(suiteId)) {
        container.applyScheduleMasterConfig(execConfig);

        const storedCsv = this.suiteUploadedCsvs.get(suiteId);
        if (storedCsv !== undefined) {
          container.file = storedCsv;
        }
        const storedApk = this.suiteUploadedApks.get(suiteId);
        if (storedApk !== undefined) {
          container.apkFile = storedApk;
          if (storedApk) {
            container.apkFileName = storedApk.name;
            container.setApkForSuite = true;
          }
        }
        const storedUseDefaultCsv = this.suiteUseDefaultCsvs.get(suiteId);
        if (storedUseDefaultCsv !== undefined) {
          container.useDefaultCsv = storedUseDefaultCsv;
        }
        const storedUseDefaultApk = this.suiteUseDefaultApks.get(suiteId);
        if (storedUseDefaultApk !== undefined) {
          container.useDefaultApk = storedUseDefaultApk;
        }
        const storedSetCsv = this.suiteSetCsvForSuites.get(suiteId);
        if (storedSetCsv !== undefined) {
          container.setCsvForSuite = storedSetCsv;
        }
        const storedSetApk = this.suiteSetApkForSuites.get(suiteId);
        if (storedSetApk !== undefined) {
          container.setApkForSuite = storedSetApk;
        }

        this.appliedSuiteConfigIds.add(suiteId);

        if (Array.isArray(execConfig.attached_files) && execConfig.attached_files.length) {
          this.suiteAttachedFilesFromSchedule.set(suiteId, execConfig.attached_files.slice());
        }

        const isMobile = Array.isArray(execConfig.execution_type)
          && execConfig.execution_type.some((t: string) => String(t).toUpperCase() === 'MOBILE');
        if (isMobile) {
          setTimeout(() => container.retryApplyScheduleEmulators(), 800);
          setTimeout(() => container.retryApplyScheduleEmulators(), 2000);
        }
        this.captureSuiteFileSnapshot(suiteId);
      } else {
        container.retryApplyScheduleEmulators();
      }
    });
    this.snapshotAllSuiteFileStates();
  }

  private captureSuiteFileSnapshot(suiteId: string): void {
    const container = this.suiteExecutionContainerRefs.get(suiteId);
    if (!container || !this.suiteOverrides.get(suiteId)) {
      return;
    }
    this.suiteFileSnapshots.set(suiteId, {
      useDefaultCsv: !!container.useDefaultCsv,
      useDefaultApk: !!container.useDefaultApk,
      hadScheduleCsv: !!container.scheduleAttachedCsvPath,
      hadScheduleApk: !!container.scheduleAttachedApkPath,
      setCsvForSuite: !!container.setCsvForSuite,
      setApkForSuite: !!container.setApkForSuite
    });
  }

  private snapshotAllSuiteFileStates(): void {
    this.suiteExecutionContainerRefs.forEach((_container, suiteId) => {
      this.captureSuiteFileSnapshot(suiteId);
    });
  }

  private updateSuiteContainerReferences(): void {
    if (this.suiteExecutionContainers && this.suiteExecutionContainers.length > 0) {
      this.suiteExecutionContainers.forEach((container: ExecutionContainerComponent) => {
        if (container.dropped_suite && container.dropped_suite._id) {
          const suiteId = container.dropped_suite._id;
          const existingRef = this.suiteExecutionContainerRefs.get(suiteId);

          // If Angular re-created the component (ngSwitch unmount/remount), the
          // instance reference changes. Clear the applied-flag so the schedule
          // config (including scheduleAttachedCsvPath) is re-applied to the
          // fresh instance instead of being skipped.
          if (existingRef && existingRef !== container) {
            this.appliedSuiteConfigIds.delete(suiteId);
          }

          this.suiteExecutionContainerRefs.set(suiteId, container);
          this.enforceSuiteOverrideIfRequired(suiteId);
          if (this.suiteOverrides.get(suiteId)) {
            container.setInitialExecutionTypes(this.getSuiteExecutionTypeOptions(suiteId));
          }
          this.seedSuiteConfigFromMasterIfNeeded(suiteId);
        }
      });
      this.applyPendingPerSuiteConfigs();
    }
  }

  private enforceSuiteOverrideIfRequired(suiteId: string): void {
    if (this.isSuiteOverrideRequired(suiteId)) {
      this.suiteOverrides.set(suiteId, true);
    }
  }

  private seedSuiteConfigFromMasterIfNeeded(suiteId: string): void {
    if (!this.suiteOverrides.get(suiteId)) {
      return;
    }
    if (this.pendingPerSuiteConfigs.has(suiteId) || this.appliedSuiteConfigIds.has(suiteId)) {
      return;
    }

    this.pendingPerSuiteConfigs.set(suiteId, this.cloneExecConfig(this.getCurrentMasterExecutionConfig()));
  }

  public onSuiteCsvStateChange(suiteId: string): void {
    this.enforceSuiteOverrideIfRequired(suiteId);
    const container = this.suiteExecutionContainerRefs.get(suiteId);
    if (container) {
      this.suiteUseDefaultCsvs.set(suiteId, !!container.useDefaultCsv);
      this.suiteSetCsvForSuites.set(suiteId, !!container.setCsvForSuite);
      this.suiteCsvResetRequesteds.set(suiteId, !!container.scheduleCsvResetRequested);
    }
  }

  public onSuiteCsvFileChange(suiteId: string, file: File | null): void {
    if (file) {
      this.suiteUploadedCsvs.set(suiteId, file);
    } else {
      this.suiteUploadedCsvs.delete(suiteId);
    }
    const container = this.suiteExecutionContainerRefs.get(suiteId);
    if (container) {
      this.suiteCsvResetRequesteds.set(suiteId, !!container.scheduleCsvResetRequested);
      this.suiteUseDefaultCsvs.set(suiteId, !!container.useDefaultCsv);
    }
  }

  public onSuiteApkFileChange(suiteId: string, file: File | null): void {
    if (file) {
      this.suiteUploadedApks.set(suiteId, file);
    } else {
      this.suiteUploadedApks.delete(suiteId);
    }
    const container = this.suiteExecutionContainerRefs.get(suiteId);
    if (container) {
      this.suiteApkResetRequesteds.set(suiteId, !!container.scheduleApkResetRequested);
      this.suiteUseDefaultApks.set(suiteId, !!container.useDefaultApk);
    }
  }

  public onSuiteParametersAvailableChange(suiteId: string, available: boolean): void {
    if (!available) {
      return;
    }

    const suite = this.suites.find(s => s.id === suiteId);
    if (suite) {
      suite.isParameterized = true;
    }
    this.cdr.detectChanges();
  }

  public isSuiteOverrideRequired(suiteId: string): boolean {
    const suite = this.suites.find(s => s.id === suiteId);
    if (!suite) {
      return false;
    }
    if (suite.isParameterized) {
      return true;
    }
    const container = this.suiteExecutionContainerRefs.get(suiteId);
    return !!container && (container.hasUploadedCsv || container.hasSuiteCsv);
  }

  get isEditMode(): boolean {
    return !!this.editScheduleId;
  }

  get saveButtonLabel(): string {
    return this.isEditMode ? 'Update schedule' : 'Save schedule';
  }

  private loadSuites(): void {
    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      console.warn('CreateScheduleComponent: projectId missing in localStorage');
      return;
    }

    this.projectService.getProjectSuites(projectId).subscribe({
      next: response => {
        const rawSuiteIds = this.pendingScheduleData && this.pendingScheduleData.suite_ids
          ? this.pendingScheduleData.suite_ids
          : [];

        const suiteIds = rawSuiteIds.map((id: any) => this.resolveSuiteId(id)).filter(Boolean);

        this.suites = response.data.map((suite: any, index: number) => ({
          id: suite._id,
          _id: suite._id,
          _type: 'suite',
          code: suite.name,
          name: suite.name,
          tag: 'web',
          selected: suiteIds.length
            ? suiteIds.indexOf(suite._id) >= 0
            : index === 0,
          csv_path: suite.csv_path,
          mobile_apk_path: suite.mobile_apk_path
        }));
        this.manualSuiteOrder = this.suites.slice();
        this.applySuiteSort();
        this.suitesLoaded = true;
        this.cdr.detectChanges();
        this.tryApplyPendingScheduleData();
      },
      error: err => {
        console.error('Failed to load project suites', err);
        this.suitesLoaded = true;
        this.tryApplyPendingScheduleData();
      }
    });
  }

  private loadScheduleForEdit(scheduleId: string): void {
    this.isLoadingSchedule = true;
    this.scheduleLoadError = '';
    this.scheduleListService.getScheduleById(scheduleId).subscribe({
      next: response => {
        this.isLoadingSchedule = false;
        if (isApiErrorResponse(response) || !response.data) {
          this.scheduleLoadError = this.notifyService.getApiErrorText(response, 'Failed to load schedule details.');
          return;
        }
        this.pendingScheduleData = response.data;
        this.tryApplyPendingScheduleData();
      },
      error: error => {
        this.isLoadingSchedule = false;
        this.scheduleLoadError = this.notifyService.getApiErrorText(error, 'Unable to load schedule details. Please try again.');
      }
    });
  }

  private tryApplyPendingScheduleData(): void {
    if (!this.pendingScheduleData || !this.suitesLoaded) {
      return;
    }
    this.populateFormFromSchedule(this.pendingScheduleData);
    this.pendingScheduleData = null;
    const applyExecConfigs = () => {
      this.applyMasterConfigToExecutionContainer();
      this.updateSuiteContainerReferences();
      this.applyPendingPerSuiteConfigs();
      this.snapshotAllSuiteFileStates();
    };
    setTimeout(applyExecConfigs, 0);
    setTimeout(applyExecConfigs, 300);
    setTimeout(applyExecConfigs, 1200);
  }

  private applyMasterConfigToExecutionContainer(): void {
    if (!this.pendingMasterConfig || !this.masterExecutionContainer) {
      return;
    }
    this.masterExecutionContainer.applyScheduleMasterConfig(this.pendingMasterConfig);
  }

  public onMasterExecutionConfigChange(): void {
    if (!this.masterExecutionContainer) {
      return;
    }

    this.pendingMasterConfig = this.cloneExecConfig(this.masterExecutionContainer.getScheduleMasterConfig());
    this.updateSuiteContainerReferences();
    this.applyPendingPerSuiteConfigs();
    this.snapshotAllSuiteFileStates();
  }

  public onSuiteConfigChange(suiteId: string): void {
    const container = this.suiteExecutionContainerRefs.get(suiteId);
    if (!container) {
      return;
    }

    this.suiteOverrides.set(suiteId, true);
    this.pendingPerSuiteConfigs.set(suiteId, this.cloneExecConfig(container.getScheduleMasterConfig()));
    this.appliedSuiteConfigIds.delete(suiteId);
    this.captureSuiteFileSnapshot(suiteId);
    this.updateMasterHasCsv();
    this.cdr.detectChanges();
  }

  private populateFormFromSchedule(data: any): void {
    this.scheduleName = data.schedule_name || '';
    this.description = data.description || '';

    this.suiteOverrides.clear();
    this.suiteExecutionTypes.clear();
    this.suiteExecutionContainerRefs.clear();
    this.appliedSuiteConfigIds.clear();
    this.expandedSuiteId = null;

    const recurrence = data.recurrence || {};
    this.originalPreviousRunTime = recurrence.previous_run_time !== undefined ? recurrence.previous_run_time : (data.previous_run_time !== undefined ? data.previous_run_time : null);
    this.originalTriggeredCount = recurrence.triggered_count !== undefined ? recurrence.triggered_count : (data.triggered_count !== undefined ? data.triggered_count : null);
    if (recurrence.repeat_type) {
      this.repeat = recurrence.repeat_type;
    }
    if (recurrence.start_date !== undefined && recurrence.start_date !== null) {
      this.startDate = this.formatDateForInput(recurrence.start_date);
    }
    if (recurrence.start_time) {
      this.startTime = recurrence.start_time;
      this.syncStartTimeToInputs(this.startTime);
    }
    if (recurrence.timezone) {
      this.timezone = this.mapTimezoneToUi(recurrence.timezone);
      this.timezoneInputValue = this.timezone;
    }
    if (recurrence.interval_value !== undefined && recurrence.interval_value !== null) {
      this.frequencyEvery = Number(recurrence.interval_value) || 1;
    }
    if (recurrence.end_type) {
      this.endCondition = this.mapEndConditionFromBackend(recurrence.end_type);
    }
    if (recurrence.end_date !== undefined && recurrence.end_date !== null) {
      this.endDate = this.formatDateForInput(recurrence.end_date);
    }
    if (recurrence.after_count !== undefined && recurrence.after_count !== null) {
      this.endAfterCount = Number(recurrence.after_count);
    }
    if (Array.isArray(recurrence.days_of_week)) {
      this.selectedWeeklyDays = recurrence.days_of_week.slice();
    }
    if (Array.isArray(recurrence.work_week_days)) {
      this.selectedWorkWeekDays = recurrence.work_week_days.slice();
    }
    if (recurrence.monthly_ordinal_mode !== undefined) {
      this.monthlyMode = recurrence.monthly_ordinal_mode ? 'ordinal' : 'fixed';
    }
    if (recurrence.day_of_month !== undefined && recurrence.day_of_month !== null) {
      this.monthlyDay = Number(recurrence.day_of_month);
      this.yearlyDay = Number(recurrence.day_of_month);
    }
    if (recurrence.ordinal_week !== undefined && recurrence.ordinal_week !== null) {
      this.monthlyOrdinalWeek = Number(recurrence.ordinal_week);
    }
    if (recurrence.day_of_week_ordinal !== undefined) {
      this.monthlyOrdinalDayOfWeek = Number(recurrence.day_of_week_ordinal);
    }
    if (recurrence.month !== undefined && recurrence.month !== null) {
      this.yearlyMonth = Number(recurrence.month);
    }
    if (recurrence.day_of_month !== undefined && recurrence.day_of_month !== null) {
      this.yearlyDay = Number(recurrence.day_of_month);
    }
    if (this.repeat === 'YEARLY') {
      this.yearlyDate = this.buildYearlyDateString(this.yearlyMonth, this.yearlyDay);
    }
     // Load backfill setting from recurrence
    if (recurrence.backfill_allowed !== undefined) {
      this.backfillAllowed = !!recurrence.backfill_allowed;
    }


    const occurrence = data.occurrence || {};
    // Schedules saved before sequential mode have no `mode` — treat as PARALLEL.
    this.executionMode = occurrence.mode === 'SEQUENTIAL' ? 'SEQUENTIAL' : 'PARALLEL';
    this.continueOnFailure = occurrence.continue_on_failure !== undefined ? !!occurrence.continue_on_failure : true;
    this.delayBetweenExecutions = this.isSequentialMode ? Number(occurrence.delay_between_sec) || 0 : 0;
    this.syncDelayPreset();
    if (occurrence.executions_per_trigger !== undefined) {
      this.executionsPerTrigger = Number(occurrence.executions_per_trigger) || 1;
    }
    if (occurrence.max_parallel_count !== undefined) {
      this.maxParallelCount = Number(occurrence.max_parallel_count) || 1;
    }
    if (occurrence.retry_on_failure !== undefined) {
      this.retryOnFailure = !!occurrence.retry_on_failure;
    }
    if (occurrence.max_retries !== undefined) {
      this.maxRetries = Number(occurrence.max_retries) || 1;
    }

    if (data.master_config) {
      this.pendingMasterConfig = data.master_config;
    }

   const suiteIds = (data.suite_ids || []).map((id: any) => this.resolveSuiteId(id)).filter(Boolean);
    this.sequenceIds = suiteIds.slice();
    this.suites = this.suites.map(suite => ({
      ...suite,
      selected: suiteIds.indexOf(suite.id) >= 0
    }));

    const perSuite = data.per_suite_configs || {};
    this.pendingPerSuiteConfigs.clear();
    this.suiteAttachedFilesFromSchedule.clear();
    this.suiteFileSnapshots.clear();
    let firstOverrideSuiteId: string | null = null;
    Object.keys(perSuite).forEach(key => {
      const suiteId = this.resolveSuiteId(perSuite[key].suite_id) || key;
      const useDefault = perSuite[key].use_default_config !== false;
      this.suiteOverrides.set(suiteId, !useDefault);
      
      if (!useDefault && perSuite[key].exec_config) {
        const execConfig = perSuite[key].exec_config;
        this.pendingPerSuiteConfigs.set(suiteId, this.cloneExecConfig(execConfig));

        const suiteTypes = { web: false, mobile: false };
        if (Array.isArray(execConfig.execution_type)) {
          execConfig.execution_type.forEach((execType: string) => {
            const normalized = String(execType).toUpperCase();
            if (normalized === 'WEB') {
              suiteTypes.web = true;
            }
            if (normalized === 'MOBILE') {
              suiteTypes.mobile = true;
            }
          });
        }
        if (suiteTypes.web || suiteTypes.mobile) {
          this.suiteExecutionTypes.set(suiteId, suiteTypes);
        }

        if (Array.isArray(execConfig.attached_files) && execConfig.attached_files.length) {
          this.suiteAttachedFilesFromSchedule.set(suiteId, execConfig.attached_files.slice());
        }
        if (!firstOverrideSuiteId) {
          firstOverrideSuiteId = suiteId;
        }
      }
    });

    if (firstOverrideSuiteId) {
      this.expandedSuiteId = firstOverrideSuiteId;
    }

    const hasMobileSuiteOverride = Object.keys(perSuite).some(key => {
      const cfg = perSuite[key] && perSuite[key].exec_config;
      return cfg && Array.isArray(cfg.execution_type)
        && cfg.execution_type.some((execType: string) => String(execType).toUpperCase() === 'MOBILE');
    });
    if (this.executionTypeArray.includes('MOBILE') || hasMobileSuiteOverride) {
      this.loadMobileDevices();
    }
  }

  private formatDateForInput(timestamp: number): string {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return this.startDate;
    }
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  private buildYearlyDateString(month: number, day: number): string {
    const year = new Date().getFullYear();
    const monthStr = String(month).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${year}-${monthStr}-${dayStr}`;
  }

  syncYearlyDateToMonthDay(): void {
    if (!this.yearlyDate) {
      return;
    }
    const parts = this.yearlyDate.split('-').map(Number);
    if (parts.length === 3) {
      this.yearlyMonth = parts[1];
      this.yearlyDay = parts[2];
    }
  }

  private mapTimezoneToUi(timezone: string): string {
    const matchedLabel = this.timezoneOptions.find(option => option.endsWith(` ${timezone}`));
    if (matchedLabel) {
      return matchedLabel;
    }
    const fallback = this.timezoneOptions.find(option => option.endsWith(' Asia/Kolkata'));
    return fallback || this.timezoneOptions[0];
  }

  private mapHubToUi(hub: string): string {
    if (!hub || hub === 'cloud') {
      return 'Xpeer Cloud';
    }
    const known = this.hubOptions.indexOf(hub);
    return known >= 0 ? hub : hub;
  }

  private mapPortToUi(port: number): string {
    if (port === 4445) {
      return 'Default Port';
    }
    const asString = String(port);
    return this.portOptions.indexOf(asString) >= 0 ? asString : asString;
  }

  private capitalizeBrowser(browser: string): string {
    if (!browser) {
      return 'Chrome';
    }
    return browser.charAt(0).toUpperCase() + browser.slice(1).toLowerCase();
  }

  private applyScreenResolutionFromApi(resolution: string): void {
    const match = BASE_RESOLUTIONS.find(r => r.name === resolution || r.value === resolution);
    if (match) {
      this.execScreenResolution = match.value;
      return;
    }
    const parts = String(resolution).split('x');
    if (parts.length === 2) {
      this.execScreenResolution = 'custom_resolution';
      this.execCustomResolutionX = Number(parts[0]) || 1920;
      this.execCustomResolutionY = Number(parts[1]) || 1080;
      return;
    }
    this.execScreenResolution = 'default_resolution';
  }

  private resolveSuiteId(value: any): string {
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (value.$oid) {
      return value.$oid;
    }
    return '';
  }

  private ensureSuiteExecutionType(suiteId: string): { web: boolean; mobile: boolean } {
    if (!this.suiteExecutionTypes.has(suiteId)) {
      this.suiteExecutionTypes.set(suiteId, { web: false, mobile: false });
    }
    return this.suiteExecutionTypes.get(suiteId)!;
  }

  setExecutionType(suiteId: string, type: 'web' | 'mobile', value: boolean): void {
    const current = this.ensureSuiteExecutionType(suiteId);
    if (type === 'web') {
      current.web = value;
    } else {
      current.mobile = value;
    }
    this.suiteExecutionTypes.set(suiteId, current);
  }

  getSuiteExecutionType(suiteId: string): { web: boolean; mobile: boolean } {
    return this.suiteExecutionTypes.get(suiteId) || { web: false, mobile: false };
  }

  onSuiteExecutionTypeChange(suiteId: string, type: 'web' | 'mobile', checked: boolean): void {
    this.setExecutionType(suiteId, type, checked);
    if (type === 'mobile' && checked) {
      this.loadMobileDevices();
    }
  }

  getSuiteExecutionTypeOptions(suiteId: string): string[] {
    const current = this.suiteExecutionTypes.get(suiteId);
    if (current && (current.web || current.mobile)) {
      const types: string[] = [];
      if (current.web) {
        types.push('web');
      }
      if (current.mobile) {
        types.push('mobile');
      }
      return types;
    }

    const pending = this.pendingPerSuiteConfigs.get(suiteId);
    if (pending && Array.isArray(pending.execution_type)) {
      const types: string[] = [];
      pending.execution_type.forEach((execType: string) => {
        const lower = String(execType).toLowerCase();
        if ((lower === 'web' || lower === 'mobile') && types.indexOf(lower) < 0) {
          types.push(lower);
        }
      });
      if (types.length) {
        return types;
      }
    }

    return this.getMasterExecutionTypes();
  }

  getMasterExecutionTypes(): string[] {
    const types = this.executionTypeArray.map(t => t.toLowerCase());
    return types.length ? types : ['web'];
  }

  get selectedSuiteCount(): number {
    return this.suites.filter(s => s.selected).length;
  }

  get selectedSuitesForExecution(): WizardSuite[] {
    return this.suites.filter(s => s.selected);
  }

  get repeatUnitLabel(): string {
    const n = this.frequencyEvery;
    const plural = (one: string, many: string) => (n === 1 ? one : many);
    switch (this.repeat) {
      case 'DAILY':
        return plural('day', 'days');
      case 'WEEKLY':
        return plural('week', 'weeks');
      case 'MONTHLY':
        return plural('month', 'months');
      case 'YEARLY':
        return plural('year', 'years');
      default:
        return 'day(s)';
    }
  }

  get recurrenceSummary(): string {
    const time = this.getStartTimeDisplay();
    const n = this.frequencyEvery;
    if (this.repeat === 'NEVER') {
      return `Your schedule will run only one time at ${time}.`;
    }
    if (this.repeat === 'DAILY') {
      if (n === 1) {
        return `Your schedule will run every day at ${time}.`;
      }
      return `Your schedule will run every ${n} days at ${time}.`;
    }
    if (this.repeat === 'WEEKLY') {
      if (n === 1) {
        return `Your schedule will run every week at ${time}.`;
      }
      return `Your schedule will run every ${n} weeks at ${time}.`;
    }
    if (n === 1) {
      return `Your schedule will run every month at ${time}.`;
    }
    return `Your schedule will run every ${n} months at ${time}.`;
  }

  get recurrenceSummaryRest(): string {
    const s = this.recurrenceSummary;
    return s.startsWith('Your ') ? s.slice(5) : s;
  }

  get nextButtonLabel(): string {
    if (this.currentStepIndex === 0) {
      return 'Next: Number of Runs';
    }
    if (this.currentStepIndex === 1) {
      return 'Next: Execution Config';
    }
    if (this.currentStepIndex === 2) {
      return 'Next: Configure';
    }
    return 'Next step';
  }

  get occurrencesSummary(): string {
    if (this.isSequentialMode) {
      return 'Each trigger will run the selected suites one after another, in the order selected.';
    }
    const n = Math.max(1, Number(this.executionsPerTrigger) || 1);
    const execWord = n === 1 ? 'execution' : 'executions';
    return `Each trigger will start ${n} ${execWord} of this suite in parallel.`;
  }

  /** Connector fill between step nodes: 0 at first step, 100% at last. */
  get stepperLinePercent(): number {
    const last = this.steps.length - 1;
    if (last <= 0) {
      return 100;
    }
    return Math.round((this.currentStepIndex / last) * 100);
  }

  get isFirstStep(): boolean {
    return this.currentStepIndex === 0;
  }

  get isLastStep(): boolean {
    return this.currentStepIndex === this.steps.length - 1;
  }

  get canGoNext(): boolean {
    return this.canProceedFrom(this.currentStepIndex);
  }

  get canSaveSchedule(): boolean {
    return this.isLastStep && this.canProceedFrom(this.currentStepIndex);
  }

  get isSaveDisabled(): boolean {
    return this.isLoadingSchedule || this.isSubmitting || !this.canSaveSchedule;
  }

  onStartDateChange(value: string): void {
    this.startDate = value;
    if (!this.isStartDateValid()) {
      return;
    }
    if (this.isTodaySelected() && !this.isStartDateTimeValid()) {
      this.startTime = this.getCurrentTimeString();
      this.syncStartTimeToInputs(this.startTime);
    }
    if (this.endDate && new Date(this.endDate) < new Date(this.startDate)) {
      this.endDate = this.startDate;
    }
    if (this.repeat === 'YEARLY' && this.yearlyDate && new Date(this.yearlyDate) < new Date(this.startDate)) {
      this.yearlyDate = this.startDate;
    }
  }

  isStartDateTimeValid(): boolean {
    if (!this.startDate || !this.startTime) {
      return false;
    }
    if (!this.isTodaySelected()) {
      return true;
    }
    const selectedDateTime = this.parseStartDateTime();
    if (!selectedDateTime) {
      return false;
    }
    return selectedDateTime >= new Date();
  }

  isStartTimeValid(): boolean {
    if (!this.startTime) {
      return false;
    }
    const parsed = this.parseTimeText(this.startTimeText);
    return parsed !== null && parsed.startTime === this.startTime;
  }

  onStartTimeKeyDown(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
      'Tab',
      ' '
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }
    if (/^[0-9APMapm:]$/.test(event.key)) {
      const current = this.startTimeText || '';
      const digits = current.replace(/\D/g, '');
      if (/^[0-9]$/.test(event.key) && digits.length >= 4 && !current.includes('AM') && !current.includes('PM')) {
        event.preventDefault();
      }
      return;
    }
    event.preventDefault();
  }

  onStartTimePaste(event: ClipboardEvent): void {
    let paste = '';
    if (event.clipboardData && event.clipboardData.getData) {
      paste = event.clipboardData.getData('text/plain') || '';
    }
    if (!/^[0-9APMapm:\s]+$/.test(paste)) {
      event.preventDefault();
    }
  }

  onEndConditionChange(value: 'NEVER' | 'TILL DATE' | 'AFTER'): void {
    this.endCondition = value;
    if (value === 'TILL DATE') {
      const base = new Date(this.startDate || this.startDateMin);
      base.setDate(base.getDate() + 1);
      const nextDay = base.toISOString().split('T')[0];
      if (!this.endDate || new Date(this.endDate) <= new Date(this.startDate)) {
        this.endDate = nextDay;
      }
    }
  }

  isStartDateValid(): boolean {
    if (!this.startDate) {
      return false;
    }
    if (!this.isDateWithinAllowedRange(this.startDate)) {
      return false;
    }
    if (this.isEditMode) {
      return true;
    }
    const selected = this.parseLocalDate(this.startDate);
    if (!selected) {
      return false;
    }
    selected.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selected >= today;
  }

  isYearlyDateValid(): boolean {
    if (!this.yearlyDate) {
      return false;
    }
    if (!this.isDateWithinAllowedRange(this.yearlyDate)) {
      return false;
    }
    return new Date(this.yearlyDate) >= new Date(this.startDate || this.startDateMin);
  }

  get isWebOnly(): boolean {
    return this.executionTypeArray.includes('WEB') && !this.executionTypeArray.includes('MOBILE');
  }

  get isMobileOnly(): boolean {
    return !this.executionTypeArray.includes('WEB') && this.executionTypeArray.includes('MOBILE');
  }

  get isWebAndMobile(): boolean {
    return this.executionTypeArray.includes('WEB') && this.executionTypeArray.includes('MOBILE');
  }

  onDescriptionChange(value: string): void {
    if (value && value[0] !== value[0].toUpperCase()) {
      this.description = value[0].toUpperCase() + value.slice(1);
    }
  }

  isScheduleNameValid(): boolean {
    // Schedule name must not be blank
    if (!(this.scheduleName || '').trim()) {
      return false;
    }
    // Allow letters, numbers, underscores, and spaces
    const validPattern = /^[a-zA-Z0-9_ ]+$/;
    return validPattern.test(this.scheduleName.trim());
  }

  isTimezoneValid(): boolean {
    const timezone = (this.timezone || '').trim();
    return timezone.length > 0 && this.timezoneOptions.includes(timezone);
  }

  isEndDateValid(): boolean {
    if (this.endCondition !== 'TILL DATE') {
      return true;
    }
    if (!this.endDate) {
      return false;
    }
    if (!this.isDateWithinAllowedRange(this.endDate)) {
      return false;
    }
    if (this.startDate && this.endDate) {
      return new Date(this.endDate) >= new Date(this.startDate);
    }
    return true;
  }

  get everyXDaysMax(): number {
    if (this.endCondition !== 'TILL DATE' || !this.startDate || !this.endDate) {
      return Infinity;
    }
    const start = this.parseLocalDate(this.startDate);
    const end = this.parseLocalDate(this.endDate);
    if (!start || !end) {
      return Infinity;
    }
    // Set to local noon to avoid daylight saving time changes affecting the day count
    start.setHours(12, 0, 0, 0);
    end.setHours(12, 0, 0, 0);
    const diffMs = end.getTime() - start.getTime();
    return Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)));
  }

  isEveryXDaysValid(): boolean {
    if (this.endCondition !== 'TILL DATE') {
      return true;
    }
    const max = this.everyXDaysMax;
    if (max === Infinity) {
      return true;
    }
    const val = Number(this.frequencyEvery);
    return val >= 1 && val <= max;
  }

  canProceedFrom(stepIndex: number): boolean {
    switch (stepIndex) {
      case 0:
        return this.selectedSuiteCount > 0 && this.isScheduleNameValid();
      case 1:
        return Number(this.executionsPerTrigger) >= 1 && this.isSequentialDelayValid;
      case 2:
        return (
          this.executionTypeArray.length > 0 &&
          this.selectedSuitesForExecution.length > 0 &&
          (!this.executionTypeArray.includes('MOBILE') || !!this.execMobileDevice) &&
          this.getSuitesMissingUpload().length === 0
        );
      case 3:
        return (
          !!(this.startDate && this.startTime && this.repeat) &&
          this.isStartTimeValid() &&
          this.isTimezoneValid() &&
          Number(this.frequencyEvery) > 0 &&
          this.isStartDateValid() &&
          this.isEndDateValid() &&
           this.isEveryXDaysValid() &&
          (this.repeat !== 'YEARLY' || this.isYearlyDateValid())
        );
      default:
        return true;
    }
  }

  private getSuitesMissingUpload(): string[] {
    const missingSuites: string[] = [];

    for (const suite of this.selectedSuitesForExecution) {
      if (!this.suiteOverrides.get(suite.id)) {
        continue;
      }

      const container = this.suiteExecutionContainerRefs.get(suite.id);
      if (container) {
        if (container.isCsvUploadRequired() || container.isApkUploadRequired()) {
          missingSuites.push(suite.name || suite.id);
        }
      } else {
        missingSuites.push(suite.name || suite.id);
      }
    }

    return missingSuites;
  }

  get suiteUploadWarning(): string | null {
    const missingSuites = this.getSuitesMissingUpload();
    if (!missingSuites.length) {
      return null;
    }
    return `The following suite${missingSuites.length > 1 ? 's require' : ' requires'} a CSV or APK upload: ${missingSuites.join(', ')}. Please upload the required file(s) or use the suite default resources before continuing.`;
  }

  onExecTypeMobileChange(checked: boolean): void {
    if (checked) {
      if (!this.executionTypeArray.includes('MOBILE')) {
        this.executionTypeArray = this.executionTypeArray.concat('MOBILE');
      }
      this.loadMobileDevices();
    } else {
      this.executionTypeArray = this.executionTypeArray.filter(t => t !== 'MOBILE');
      this.mobileDeviceOptions = [];
      this.execMobileDevice = '';
      this.mobileDeviceLoadError = '';
    }
  }

  onSuiteOverrideChange(suiteId: string, checked: boolean): void {
    if (this.isSuiteOverrideRequired(suiteId)) {
      this.suiteOverrides.set(suiteId, true);
      return;
    }

    this.suiteOverrides.set(suiteId, checked);
    if (checked) {
      const current = this.ensureSuiteExecutionType(suiteId);
      if (!current.web && !current.mobile) {
        const masterTypes = this.masterExecutionContainer && this.masterExecutionContainer.selectedExecutionTypes ? this.masterExecutionContainer.selectedExecutionTypes : [];
        current.web = masterTypes.includes('web') || this.executionTypeArray.includes('WEB');
        current.mobile = masterTypes.includes('mobile') || this.executionTypeArray.includes('MOBILE');
        this.suiteExecutionTypes.set(suiteId, current);
      }

      this.seedSuiteConfigFromMasterIfNeeded(suiteId);

      const container = this.suiteExecutionContainerRefs.get(suiteId);
      if (container) {
        container.setInitialExecutionTypes(this.getSuiteExecutionTypeOptions(suiteId));
        setTimeout(() => this.applyPendingPerSuiteConfigs(), 0);
      }

      if (current.mobile) {
        this.loadMobileDevices();
      }
    } else {
      // Clear the stored container reference when override is unchecked
      this.suiteExecutionContainerRefs.delete(suiteId);
      this.appliedSuiteConfigIds.delete(suiteId);
    }
  }

  onMasterExecutionTypesChange(types: string[]): void {
    this.executionTypeArray = types.map(type => type.toUpperCase());

    for (const suite of this.selectedSuitesForExecution) {
      const current = this.ensureSuiteExecutionType(suite.id);
      if (!current.web && !current.mobile) {
        current.web = types.includes('web');
        current.mobile = types.includes('mobile');
        this.suiteExecutionTypes.set(suite.id, current);
      }

      const container = this.suiteExecutionContainerRefs.get(suite.id);
      if (container) {
        container.setInitialExecutionTypes(this.getSuiteExecutionTypeOptions(suite.id));
      }
    }
  }

  public getCurrentMasterExecutionConfig(): any {
    if (this.pendingMasterConfig) {
      return this.cloneExecConfig(this.pendingMasterConfig);
    }

    if (this.masterExecutionContainer) {
      return this.masterExecutionContainer.getScheduleMasterConfig();
    }

    return this.buildMasterConfigForPayload();
  }

  public getSuiteExecutionConfig(suiteId: string): any {
    const suiteConfig = this.pendingPerSuiteConfigs.get(suiteId);
    if (suiteConfig) {
      return suiteConfig;
    }

    return this.getCurrentMasterExecutionConfig();
  }

  private loadMobileDevices(): void {
    this.isLoadingMobileDevices = true;
    this.mobileDeviceLoadError = '';
    this.mobileDeviceOptions = [];

    this.mobileStreamService.getMobileExecutionList({}).subscribe({
      next: (response: any) => {
        this.isLoadingMobileDevices = false;
        if (response && response.operationStatus === 'SUCCESS' && Array.isArray(response.data)) {
          this.mobileDeviceOptions = response.data.map((device: any) => {
            const name = device.device_name || device.domain || device.udid || 'Unknown device';
            const domain = device.domain ? device.domain : '';
            return domain ? `${domain} - ${name}` : name;
          });
          if (!this.execMobileDevice && this.mobileDeviceOptions.length > 0) {
            this.execMobileDevice = this.mobileDeviceOptions[0];
          }
          return;
        }
        this.mobileDeviceLoadError = 'No mobile devices available.';
      },
      error: () => {
        this.isLoadingMobileDevices = false;
        this.mobileDeviceLoadError = 'Unable to load mobile device list. Please try again.';
      }
    });
  }

  bumpExecutionsPerTrigger(delta: number): void {
    const next = Number(this.executionsPerTrigger) + delta;
    const clamped = Math.min(99, Math.max(1, Math.floor(Number.isFinite(next) ? next : 1)));
    this.executionsPerTrigger = clamped;
  }

  /**
   * Prevents typing of invalid characters (minus sign, decimal point, 0 as first digit, etc).
   * Only allows digits 1-9 as first digit, then 0-9 for subsequent digits.
   */
  preventInvalidInput(event: KeyboardEvent): void {
    const key = event.key;
    const input = event.target as HTMLInputElement;
    const currentValue = input.value.trim();
    
    // Block minus sign, decimal point, and other non-numeric keys
    if (key === '-' || key === '.' || key === ',' || key === 'e' || key === 'E' || key === '+') {
      event.preventDefault();
      return;
    }
    
    // Block "0" if field is empty (not allowed as first digit)
    if (key === '0' && currentValue === '') {
      event.preventDefault();
    }
  }

  /**
   * Validates and enforces positive integer input (>= 1, no decimals).
   * Applied to trigger count and frequency every fields.
   */
  validatePositiveInteger(field: 'endAfterCount' | 'frequencyEvery', value: any): void {
    const numValue = Number(value);
    
    // Convert to integer (floor), ensure at least 1
    const validValue = Math.max(1, Math.floor(Number.isFinite(numValue) ? numValue : 1));
    
    if (field === 'endAfterCount') {
      this.endAfterCount = validValue;
    } else {
      this.frequencyEvery = validValue;
    }
  }

  onApkSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files && input.files.length > 0 ? input.files : null;
    const file = files ? files[0] : null;
    if (file) {
      this.execApkFile = file;
      this.execApkFileName = file.name;
    }
  }

  clearApkFile(input: HTMLInputElement): void {
    input.value = '';
    this.execApkFile = undefined;
    this.execApkFileName = '';
  }

  get filteredSuites(): WizardSuite[] {
    const filter = (this.suiteFilterText || '').trim().toLowerCase();
    if (!filter) {
      return this.suites;
    }
    return this.suites.filter(suite =>
      suite.name.toLowerCase().includes(filter) || suite.code.toLowerCase().includes(filter)
    );
  }

  trackBySuiteId(_index: number, suite: WizardSuite): string {
    return suite.id;
  }

  dropSuiteOrder(event: CdkDragDrop<WizardSuite[]>): void {
    const visibleSuites = this.filteredSuites;
    const movedSuite = visibleSuites[event.previousIndex];
    const targetSuite = visibleSuites[event.currentIndex];
    if (!movedSuite || !targetSuite) {
      return;
    }
    const previousIndex = this.suites.indexOf(movedSuite);
    const currentIndex = this.suites.indexOf(targetSuite);
    moveItemInArray(this.suites, previousIndex, currentIndex);
    this.sortOption = 'CUSTOM';
    this.manualSuiteOrder = this.suites.slice();
  }

  onSortOptionChange(value: 'CUSTOM' | 'NAME_ASC' | 'NAME_DESC'): void {
    this.sortOption = value;
    if (value === 'CUSTOM') {
      if (this.manualSuiteOrder.length) {
        this.suites = this.manualSuiteOrder.slice();
      }
      return;
    }
    if (!this.manualSuiteOrder.length) {
      this.manualSuiteOrder = this.suites.slice();
    }
    this.applySuiteSort();
  }

  applySuiteSort(): void {
    if (this.sortOption === 'CUSTOM') {
      this.suites = this.manualSuiteOrder.slice();
      return;
    }
    const compareName = (a: WizardSuite, b: WizardSuite): number =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

    if (this.sortOption === 'NAME_ASC') {
      this.suites = this.suites.slice().sort(compareName);
    } else {
      this.suites = this.suites.slice().sort((a, b) => compareName(b, a));
    }
  }

  onSuiteSelectionChange(suite: WizardSuite): void {
    if (suite.selected) {
      this.checkSuiteParameterization(suite);
      this.loadSuiteDetails(suite);
    } else {
      suite.isParameterized = false;
    }
  }

  private checkSuiteParameterization(suite: WizardSuite): void {
    const db = 1;
    this.parameterService.findBySuiteId(suite.id, db).subscribe({
      next: (response: any) => {
        if (!response || !response.data) {
          suite.isParameterized = false;
          return;
        }
        suite.isParameterized = this.isParametersAvailableInData(response.data);
        if (suite.isParameterized && !this.suiteOverrides.get(suite.id)) {
          this.suiteOverrides.set(suite.id, true);
        }
      },
      error: () => {
        suite.isParameterized = false;
      }
    });
  }

  private loadSelectedSuiteDetails(): void {
    this.selectedSuitesForExecution.forEach(suite => {
      this.loadSuiteDetails(suite);
    });
  }

  private loadSuiteDetails(suite: WizardSuite): void {
    if (suite.csv_path || suite.mobile_apk_path) {
      return;
    }

    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      return;
    }

    this.projectService.getProjectSuite(projectId, suite.id).subscribe({
      next: (response: any) => {
        if (!response || !response.data) {
          return;
        }

        const details = response.data;
        const updatedSuite: WizardSuite = {
          ...suite,
          csv_path: details.csv_path,
          mobile_apk_path: details.mobile_apk_path
        };

        const index = this.suites.findIndex(s => s.id === suite.id);
        if (index !== -1) {
          this.suites[index] = updatedSuite;
        }

        const manualIndex = this.manualSuiteOrder.findIndex(s => s.id === suite.id);
        if (manualIndex !== -1) {
          this.manualSuiteOrder[manualIndex] = updatedSuite;
        }
      },
      error: () => {
        // Ignore missing suite details; the execution container will still render without CSV metadata.
      }
    });
  }

  private isParametersAvailableInData(data: any[]): boolean {
    const PLACEHOLDER_PATTERN = /\{\{@@[^}]+}}/;
    if (!Array.isArray(data)) {
      return false;
    }

    for (const group of data) {
      if (!group || !Array.isArray(group.commandSteps)) {
        continue;
      }

      for (const step of group.commandSteps) {
        if (step.active === false) {
          continue;
        }

        if (step.type === 'API') {
          if (step.target && step.target.request && step.target.request.auth && step.target.request.auth.type) {
            const authType = step.target.request.auth.type;
            if (this.containsParameterPattern(step.target.request.auth[authType], PLACEHOLDER_PATTERN)) {
              return true;
            }
          }

          if (step.target && step.target.request && this.containsParameterPattern(step.target.request.headers, PLACEHOLDER_PATTERN)) {
            return true;
          }

          if (step.target && step.target.request && step.target.request.url && PLACEHOLDER_PATTERN.test(step.target.request.url.raw)) {
            return true;
          }

          if (step.target && step.target.request && step.target.request.body && step.target.request.body.mode) {
            const bodyMode = step.target.request.body.mode;
            switch (bodyMode) {
              case 'raw':
                if (PLACEHOLDER_PATTERN.test(step.target.request.body.raw)) {
                  return true;
                }
                break;
              case 'formdata':
                if (this.containsParameterPattern(step.target.request.body.formdata, PLACEHOLDER_PATTERN)) {
                  return true;
                }
                break;
              case 'urlencoded':
                if (this.containsParameterPattern(step.target.request.body.urlencoded, PLACEHOLDER_PATTERN)) {
                  return true;
                }
                break;
              case 'file':
                if (step.target.request.body.file && PLACEHOLDER_PATTERN.test(step.target.request.body.file.src)) {
                  return true;
                }
                break;
              case 'graphql':
                if ((step.target.request.body.graphql && PLACEHOLDER_PATTERN.test(step.target.request.body.graphql.query)) ||
                  (step.target.request.body.graphql && PLACEHOLDER_PATTERN.test(step.target.request.body.graphql.variables))) {
                  return true;
                }
                break;
            }
          }
        } else {
          if (typeof step.value === 'string' && step.value.startsWith('@@')) {
            return true;
          }
          if (typeof step.value2 === 'string' && step.value2.startsWith('@@')) {
            return true;
          }
          if (this.updateParameterPresentInTarget(step.target) || this.updateParameterPresentInTarget(step.target2)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  private containsParameterPattern(target: any, pattern: RegExp): boolean {
    if (!target) {
      return false;
    }
    if (Array.isArray(target)) {
      return target.some(item => this.containsParameterPattern(item, pattern));
    }
    if (typeof target === 'string') {
      return pattern.test(target);
    }
    if (typeof target === 'object') {
      return Object.values(target).some(value => this.containsParameterPattern(value, pattern));
    }
    return false;
  }

  private updateParameterPresentInTarget(target: any): boolean {
    if (!target || typeof target !== 'object') {
      return false;
    }

    const props = ['dom_id', 'dom_name', 'tag', 'css', 'class', 'desc', 'link', 'partial_link', 'horizontal_index'];
    for (const prop of props) {
      if (typeof target[prop] === 'string' && target[prop].startsWith('@@')) {
        return true;
      }
    }

    if (Array.isArray(target.xpaths)) {
      return target.xpaths.some((xpath: any) => typeof xpath === 'string' && xpath.startsWith('@@'));
    }

    return false;
  }

  /** Jump back freely; jump forward only if intermediate steps validate. */
  goToStep(index: number): void {
    if (index < 0 || index >= this.steps.length) {
      return;
    }
    if (index <= this.currentStepIndex) {
      this.captureExecutionStepStateIfLeaving(index);
      this.currentStepIndex = index;
      this.onExecutionStepActivated(index);
      return;
    }
    for (let s = this.currentStepIndex; s < index; s++) {
      if (!this.canProceedFrom(s)) {
        return;
      }
    }
    this.captureExecutionStepStateIfLeaving(index);
    this.currentStepIndex = index;
    this.onExecutionStepActivated(index);
  }

  next(): void {
    if (!this.canGoNext || this.isLastStep) {
      return;
    }
    this.captureExecutionStepStateIfLeaving(this.currentStepIndex + 1);
    this.currentStepIndex++;
    this.onExecutionStepActivated(this.currentStepIndex);
  }

  private captureExecutionStepStateIfLeaving(targetStepIndex: number): void {
    if (this.currentStepIndex !== 2 || targetStepIndex === 2) {
      return;
    }

    if (this.masterExecutionContainer) {
      this.pendingMasterConfig = this.cloneExecConfig(this.masterExecutionContainer.getScheduleMasterConfig());
    }

    this.selectedSuitesForExecution.forEach(suite => {
      const container = this.suiteExecutionContainerRefs.get(suite.id);
      if (container) {
        this.suiteUseDefaultCsvs.set(suite.id, !!container.useDefaultCsv);
        this.suiteUseDefaultApks.set(suite.id, !!container.useDefaultApk);
        this.suiteSetCsvForSuites.set(suite.id, !!container.setCsvForSuite);
        this.suiteSetApkForSuites.set(suite.id, !!container.setApkForSuite);
        this.suiteCsvResetRequesteds.set(suite.id, !!container.scheduleCsvResetRequested);
        this.suiteApkResetRequesteds.set(suite.id, !!container.scheduleApkResetRequested);
      }

      if (!this.suiteOverrides.get(suite.id)) {
        return;
      }

      this.pendingPerSuiteConfigs.set(suite.id, this.buildSuiteExecConfig(suite.id));
      this.appliedSuiteConfigIds.delete(suite.id);
    });
  }

  private onExecutionStepActivated(stepIndex: number): void {
    if (stepIndex === 2) {
      this.selectedSuitesForExecution.forEach(suite => {
        if (suite.selected) {
          this.checkSuiteParameterization(suite);
        }
      });
      this.loadSelectedSuiteDetails();
      setTimeout(() => {
        this.applyMasterConfigToExecutionContainer();
        this.updateSuiteContainerReferences();
        this.applyPendingPerSuiteConfigs();
        setTimeout(() => this.updateMasterHasCsv(), 0);
      }, 0);
      setTimeout(() => {
        this.updateSuiteContainerReferences();
        this.applyPendingPerSuiteConfigs();
        this.snapshotAllSuiteFileStates();
      }, 400);
    }
  }

  private updateMasterHasCsv(): void {
    if (!this.masterExecutionContainer) {
      this.masterHasCsv = false;
      return;
    }
    const m = this.masterExecutionContainer;
    this.masterHasCsv = !!(m.isDefaultCsvFile || m.hasSuiteCsv || m.useDefaultCsv || m.setCsvForSuite);
  }

  prev(): void {
    if (!this.isFirstStep) {
      this.currentStepIndex--;
      this.onExecutionStepActivated(this.currentStepIndex);
    }
  }

  finish(): void {
    if (this.isSubmitting || !this.canSaveSchedule) {
      return;
    }

    // Capture current execution-step state for any containers still mounted
    // (covers the case where finish() is called without leaving step 3 first)
    if (this.masterExecutionContainer) {
      this.pendingMasterConfig = this.cloneExecConfig(
        this.masterExecutionContainer.getScheduleMasterConfig()
      );
    }
    this.selectedSuitesForExecution.forEach(suite => {
      const container = this.suiteExecutionContainerRefs.get(suite.id);
      if (!container) { return; }
      this.suiteUseDefaultCsvs.set(suite.id, !!container.useDefaultCsv);
      this.suiteUseDefaultApks.set(suite.id, !!container.useDefaultApk);
      this.suiteSetCsvForSuites.set(suite.id, !!container.setCsvForSuite);
      this.suiteSetApkForSuites.set(suite.id, !!container.setApkForSuite);
      if (container.hasUploadedCsv && !this.suiteUploadedCsvs.has(suite.id)) {
        const f = container.getUploadedCsvFile ? container.getUploadedCsvFile() : null;
        if (f) { this.suiteUploadedCsvs.set(suite.id, f); }
      }
      if (container.apkFile && !this.suiteUploadedApks.has(suite.id)) {
        this.suiteUploadedApks.set(suite.id, container.apkFile);
      }
    });
    this.isSubmitting = true;

    const projectId = localStorage.getItem('projectId');
    const licenseId = localStorage.getItem('licenseId');

    if (!projectId) {
      console.error('Project ID not found in localStorage');
      this.isSubmitting = false;
      return;
    }

    // Get selected suite IDs
    // In SEQUENTIAL mode the order of suite_ids is the run order.
    const suiteIds = (this.isSequentialMode ? this.sequencedSuites : this.selectedSuitesForExecution).map(s => s.id);

    // Parse start date to timestamp (milliseconds) and apply a 5-minute future adjustment for today if needed
    const [year, month, day] = this.startDate.split('-').map(Number);
    const adjustedStart = this.getAdjustedStartDateTime(year, month, day);

    // Extract timezone label
    const timezoneLabel = this.extractTimezoneLabel(this.timezone);

    // Build recurrence object
    const recurrence: any = {
      repeat_type: this.repeat,
      start_date: adjustedStart.startDateTimestamp,
      start_time: adjustedStart.startTime,
      timezone: timezoneLabel,
      interval_value: Number(this.frequencyEvery),
      end_type: this.mapEndConditionToBackend(this.endCondition),
      backfill_allowed: this.backfillAllowed  // ← ADD BACKFILL HERE
    };

    // Add end_date if TILL DATE
    if (this.endCondition === 'TILL DATE' && this.endDate) {
      const [eYear, eMonth, eDay] = this.endDate.split('-').map(Number);
      const [startHour, startMinute] = adjustedStart.startTime.split(':').map(Number);
      const endDateTime = new Date(eYear, eMonth - 1, eDay, startHour, startMinute);
      recurrence.end_date = endDateTime.getTime();
    }

    // Add after_count if AFTER
    if (this.endCondition === 'AFTER') {
      recurrence.after_count = Number(this.endAfterCount);
      recurrence.triggered_count = this.isEditMode && this.originalTriggeredCount !== null
        ? this.originalTriggeredCount
        : 0;
    }

    // Preserve execution history fields when editing
    if (this.isEditMode) {
      if (this.originalPreviousRunTime !== null) {
        recurrence.previous_run_time = this.originalPreviousRunTime;
      }
      if (this.originalTriggeredCount !== null && this.endCondition !== 'AFTER') {
        recurrence.triggered_count = this.originalTriggeredCount;
      }
    }

    // Add days_of_week for WEEKLY
    if (this.repeat === 'WEEKLY' && this.selectedWeeklyDays.length > 0) {
      recurrence.days_of_week = this.selectedWeeklyDays;
    }

    // Add work_week_days for WORK WEEK
    if (this.repeat === 'WORK WEEK') {
      recurrence.work_week_days = this.selectedWorkWeekDays;
    }

    // Add monthly fields
    if (this.repeat === 'MONTHLY') {
      recurrence.monthly_ordinal_mode = this.monthlyMode === 'ordinal';
      if (this.monthlyMode === 'fixed') {
        recurrence.day_of_month = Number(this.monthlyDay);
      } else {
        recurrence.ordinal_week = Number(this.monthlyOrdinalWeek);
        recurrence.day_of_week_ordinal = Number(this.monthlyOrdinalDayOfWeek);
      }
    }

    // Add yearly fields
    if (this.repeat === 'YEARLY') {
      if (this.yearlyDate) {
        const [year, month, day] = this.yearlyDate.split('-').map(Number);
        recurrence.month = month;
        recurrence.day_of_month = day;
      } else {
        recurrence.month = Number(this.yearlyMonth);
        recurrence.day_of_month = Number(this.yearlyDay);
      }
    }

    const masterConfig = this.buildMasterConfigForPayload();

    // Build the payload
    const payload = {
      project_id: projectId,
      suite_ids: suiteIds,
      schedule_name: this.scheduleName,
      description: this.description,
      license_id: licenseId || '',

      recurrence: recurrence,

      occurrence: this.isSequentialMode
        ? {
            mode: 'SEQUENTIAL',
            continue_on_failure: this.continueOnFailure,
            delay_between_sec: Number(this.delayBetweenExecutions) || 0
          }
        : {
            mode: 'PARALLEL',
            executions_per_trigger: Number(this.executionsPerTrigger),
            delay_between_sec: 0,
            max_parallel_count: Number(this.maxParallelCount),
            retry_on_failure: this.retryOnFailure,
            max_retries: Number(this.maxRetries)
          },

      master_config: masterConfig,

      per_suite_configs: this.buildPerSuiteConfigs(suiteIds)
    };

    console.log('Schedule payload:', payload);
    if (this.isEditMode && this.editScheduleId) {
      this.updateScheduleOnBackend(this.editScheduleId, payload);
    } else {
      this.createSchedule(payload);
    }
  }

  private buildMasterConfigForPayload(): any {
    if (this.pendingMasterConfig) {
      return this.cloneExecConfig(this.pendingMasterConfig);
    }

    if (this.masterExecutionContainer) {
      return this.masterExecutionContainer.getScheduleMasterConfig();
    }

    const portValue = this.parsePortValue(this.execPort);
    const screenResolution = this.parseScreenResolution();
    const browsers = this.executionTypeArray.includes('WEB')
      ? (Array.isArray(this.execBrowser) ? this.execBrowser.map(b => b.toLowerCase()) : [])
      : null;

    const masterConfig: any = {
      execution_type: this.executionTypeArray,
      hub: this.execHub === 'Xpeer Cloud' ? 'cloud' : this.execHub,
      port: portValue,
      run_on_xpeer_hub: this.optRunOnXpeerHub,
      send_notification: this.optNotifyOnCompletion,
      visual_comparison: this.optVisualComparison,
      is_implicit_default_pause: this.execImplicitDelay,
      default_pause: this.execDelaySeconds,
      execution_machine: null
    };

    if (this.executionTypeArray.includes('WEB')) {
      masterConfig.browsers = browsers;
      masterConfig.screen_resolution = screenResolution;
    } else {
      masterConfig.browsers = null;
      masterConfig.screen_resolution = null;
    }

    masterConfig.emulators = this.executionTypeArray.includes('MOBILE') ? [] : null;
    return masterConfig;
  }

  private buildEmulatorsList(): any[] {
    return [];
  }

  toggleWeeklyDay(dayValue: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedWeeklyDays.includes(dayValue)) {
        this.selectedWeeklyDays = this.selectedWeeklyDays.concat(dayValue);
      }
    } else {
      this.selectedWeeklyDays = this.selectedWeeklyDays.filter(d => d !== dayValue);
    }
  }

  toggleWorkWeekDay(dayValue: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedWorkWeekDays.includes(dayValue)) {
        this.selectedWorkWeekDays = this.selectedWorkWeekDays.concat(dayValue);
      }
    } else {
      this.selectedWorkWeekDays = this.selectedWorkWeekDays.filter(d => d !== dayValue);
    }
  }

  toggleExecutionType(type: string, checked: boolean): void {
    if (checked) {
      if (!this.executionTypeArray.includes(type)) {
        this.executionTypeArray = this.executionTypeArray.concat(type);
      }
    } else {
      this.executionTypeArray = this.executionTypeArray.filter(t => t !== type);
    }
  }

  private extractTimezoneLabel(timezoneString: string): string {
    const parts = timezoneString.split(') ');
    return parts.length === 2 ? parts[1] : timezoneString;
  }

  getStartTimeDisplay(): string {
    if (!this.startDate || !this.startTime || !this.isTimezoneValid()) {
      return '—';
    }

    const selectedTz = this.extractTimezoneLabel(this.timezone);
    const [hour, minute] = this.startTime.split(':').map(Number);
    const startDateTime = moment.tz(`${this.startDate} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`, 'YYYY-MM-DD HH:mm', selectedTz);

    const localTz = moment.tz.guess();
    const localTime = startDateTime.clone().tz(localTz);
    const selectedTime = startDateTime.clone().tz(selectedTz);

    const localFormatted = localTime.format('HH:mm');
    const selectedFormatted = selectedTime.format('HH:mm');

    if (localFormatted === selectedFormatted) {
      return `${selectedFormatted} (${selectedTz})`;
    }

    return `${selectedFormatted} (${selectedTz}) / ${localFormatted} (${localTz})`;
  }

  private mapEndConditionToBackend(uiValue: string): string {
    // Map UI end condition values to backend values
    const endConditionMap: any = {
      'NEVER': 'NEVER',
      'TILL DATE': 'ON_DATE',
      'AFTER': 'AFTER_N'
    };
    return endConditionMap[uiValue] || 'NEVER';
  }

  private mapEndConditionFromBackend(backendValue: string): 'NEVER' | 'TILL DATE' | 'AFTER' {
    const reverseMap: any = {
      'NEVER': 'NEVER',
      'ON_DATE': 'TILL DATE',
      'AFTER_N': 'AFTER'
    };
    return reverseMap[backendValue] || 'NEVER';
  }  

  private parsePortValue(portString: string): number {
    const portMap: any = {
      'Xpeer Cloud': 4445,
      'Default Port': 4445,
      '443': 443,
      '4444': 4444,
      '8080': 8080
    };
    return portMap[portString] || 4445;
  }

  private parseScreenResolution(): string {
    if(this.execScreenResolution === 'default_resolution') {
      return '1920x1200';
    }
    if (this.execScreenResolution === 'custom_resolution') {
      return `${this.execCustomResolutionX}x${this.execCustomResolutionY}`;
    }
    // Find from BASE_RESOLUTIONS
    const resolution = BASE_RESOLUTIONS.find(r => r.value === this.execScreenResolution);
    return resolution ? resolution.name : '1920x1200';
  }

  private buildPerSuiteConfigs(suiteIds: string[]): any {
    const perSuiteConfigs: any = {};
    suiteIds.forEach(suiteId => {
      perSuiteConfigs[suiteId] = {
        suite_id: suiteId,
        use_default_config: !this.suiteOverrides.get(suiteId),
        exec_config: this.suiteOverrides.get(suiteId) ? this.buildSuiteExecConfig(suiteId) : null
      };
    });
    return perSuiteConfigs;
  }

  private buildSuiteExecConfig(suiteId: string): any {
    const container = this.suiteExecutionContainerRefs.get(suiteId);
    const savedAttachedFiles = this.suiteAttachedFilesFromSchedule.get(suiteId);
    const pendingConfig = this.pendingPerSuiteConfigs.get(suiteId);

    if (!container) {
      console.warn(`Suite execution container not found for suite ${suiteId}`);
      if (pendingConfig) {
        return this.cloneExecConfig(pendingConfig);
      }
      return null;
    }

    // Sync file references back from parent stored maps in case container was unmounted/re-created
    const storedCsv = this.suiteUploadedCsvs.get(suiteId);
    if (storedCsv !== undefined) {
      container.file = storedCsv;
    }
    const storedApk = this.suiteUploadedApks.get(suiteId);
    if (storedApk !== undefined) {
      container.apkFile = storedApk;
    }

    const execConfig = container.getScheduleMasterConfig();
    if (!execConfig) {
      return pendingConfig ? this.cloneExecConfig(pendingConfig) : null;
    }

    if ((!execConfig.emulators || !execConfig.emulators.length) && pendingConfig && pendingConfig.emulators) {
      execConfig.emulators = pendingConfig.emulators.slice();
    }

    const hasNewCsvUpload = container.hasUploadedCsv || container.setCsvForSuite;
    const hasNewApkUpload = !!container.apkFile || container.setApkForSuite;

    if (hasNewCsvUpload || hasNewApkUpload) {
      execConfig.attached_files = null;
    } else if (savedAttachedFiles && savedAttachedFiles.length) {
      execConfig.attached_files = savedAttachedFiles.slice();
    } else {
      execConfig.attached_files = null;
    }

    return execConfig;
  }

  private cloneExecConfig(config: any): any {
    if (!config) {
      return null;
    }
    try {
      return JSON.parse(JSON.stringify(config));
    } catch (e) {
      return config;
    }
  }

  private navigateToScheduleList(): void {
    this.router.navigate(['/scheduler/schedule-list']);
  }

  private updateScheduleOnBackend(scheduleId: string, payload: any): void {
    this.scheduleListService.updateSchedule(scheduleId, payload).subscribe({
      next: (response: any) => {
        if (isApiErrorResponse(response)) {
          this.isSubmitting = false;
          this.notifyService.notifyApiError('Failed', response, 'Failed to update schedule. Please try again.');
          return;
        }
        this.runPostSaveFileUploads(scheduleId, 'Schedule updated successfully.');
      },
      error: error => {
        this.isSubmitting = false;
        this.notifyService.notifyApiError('Failed', error, 'Failed to update schedule. Please try again.');
      }
    });
  }

  private createSchedule(payload: any): void {
    this.scheduleCreateService.createSchedule(payload).subscribe({
      next: (response: any) => {
        if (isApiErrorResponse(response)) {
          this.isSubmitting = false;
          this.notifyService.notifyApiError('Failed', response, 'Failed to create schedule. Please try again.');
          return;
        }

        const scheduleId = response && response.data && response.data.id ? response.data.id : null;
        if (!scheduleId) {
          this.isSubmitting = false;
          this.notifyService.notifyApiError('Failed', response, 'Failed to create schedule. Please try again.');
          return;
        }

        this.runPostSaveFileUploads(scheduleId, 'Schedule created successfully.');
      },
      error: error => {
        this.isSubmitting = false;
        this.notifyService.notifyApiError('Failed', error, 'Failed to create schedule. Please try again.');
      }
    });
  }

  /**
   * After create/update succeeds, upload CSV/APK per suite when needed.
   * Create: all selected suites. Edit: only suites with override + file-related changes.
   */
  private runPostSaveFileUploads(scheduleId: string, scheduleSavedMessage: string): void {
    const suitesForUpload = this.getSuitesForFileUpload();

    if (!suitesForUpload.length) {
      this.notifyService.notify('success', 'Success', scheduleSavedMessage);
      this.navigateToScheduleList();
      return;
    }

    // Sync file references back from parent stored maps in case container was unmounted/re-created
    suitesForUpload.forEach(suite => {
      const container = this.suiteExecutionContainerRefs.get(suite.id);
      if (container) {
        const storedCsv = this.suiteUploadedCsvs.get(suite.id);
        if (storedCsv !== undefined) {
          container.file = storedCsv;
        }
        const storedApk = this.suiteUploadedApks.get(suite.id);
        if (storedApk !== undefined) {
          container.apkFile = storedApk;
        }
        const storedUseDefaultCsv = this.suiteUseDefaultCsvs.get(suite.id);
        if (storedUseDefaultCsv !== undefined) {
          container.useDefaultCsv = storedUseDefaultCsv;
        }
        const storedUseDefaultApk = this.suiteUseDefaultApks.get(suite.id);
        if (storedUseDefaultApk !== undefined) {
          container.useDefaultApk = storedUseDefaultApk;
        }
      }
    });

    this.scheduleCreateService.uploadFiles(
      scheduleId,
      suitesForUpload,
      this.execApkFile,
      this.suiteExecutionContainerRefs
    ).subscribe(
      (res: any) => {
        if (res && res.error) {
          this.notifyService.notifyApiError('Upload Failed', res.err, 'Schedule saved but file upload failed.');
          this.navigateToScheduleList();
          return;
        }

        const failed = res && res.failed ? res.failed : [];
        if (failed.length === 0) {
          this.notifyService.notify('success', 'Success', scheduleSavedMessage + ' Files uploaded successfully.');
        } else {
          const firstFailed = failed[0];
          this.notifyService.notifyApiWarning(
            'Partial',
            firstFailed && firstFailed.err,
            'Schedule saved but some file uploads failed.'
          );
        }
        this.navigateToScheduleList();
      },
      error => {
        this.notifyService.notifyApiError('Upload Failed', error, 'Schedule saved but file upload failed.');
        this.navigateToScheduleList();
      }
    );
  }

  /**
   * suite Added isSuiteParameterized(suite) to consolidate and determine if a suite is parameterized (i.e., requires/uses a CSV or an APK file)
   */
 private isSuiteParameterized(suite: WizardSuite): boolean {
  if (!suite) {
    return false;
  }

  const container = this.suiteExecutionContainerRefs.get(suite.id);
  const snapshot = this.suiteFileSnapshots.get(suite.id);

  // ── CSV side ──────────────────────────────────────────────────────────
  const uploadedCsv = this.suiteUploadedCsvs.get(suite.id);
  const setCsv = this.suiteSetCsvForSuites.get(suite.id);
  const useDefaultCsv = this.suiteUseDefaultCsvs.get(suite.id);

  const hasCsvAction = !!(
    uploadedCsv ||                                          // new file uploaded
    setCsv ||                                               // "set as suite default" flagged
    useDefaultCsv ||                                        // user chose "use suite default CSV"
    (container && (
      container.hasUploadedCsv ||
      container.setCsvForSuite ||
      container.useDefaultCsv                              // container flag still mounted
    )) ||
    (snapshot && snapshot.hadScheduleCsv)                  // edit: had attached CSV
  );

  // ── APK / mobile side ─────────────────────────────────────────────────
  const uploadedApk = this.suiteUploadedApks.get(suite.id);
  const setApk = this.suiteSetApkForSuites.get(suite.id);
  const useDefaultApk = this.suiteUseDefaultApks.get(suite.id);

  const hasMobileAction = !!(
    uploadedApk ||                                          // new APK uploaded
    setApk ||                                               // "set as suite default" flagged
    useDefaultApk ||                                        // user chose "use suite default APK"
    (container && (
      container.apkFile ||
      container.setApkForSuite ||
      container.useDefaultApk                              // container flag still mounted
    )) ||
    (snapshot && snapshot.hadScheduleApk)                  // edit: had attached APK
  );

  return hasCsvAction || hasMobileAction;
}
  /** Suites that should receive a POST /schedules/files after save. */
  private getSuitesForFileUpload(): WizardSuite[] {
    if (!this.selectedSuitesForExecution || !this.selectedSuitesForExecution.length) {
      return [];
    }

    if (!this.isEditMode) {
      return this.selectedSuitesForExecution.filter(suite => this.isSuiteParameterized(suite));
    }

    return this.selectedSuitesForExecution.filter(suite => {
      if (!this.isSuiteParameterized(suite)) {
        return false;
      }
      if (this.execApkFile) {
        const container = this.suiteExecutionContainerRefs.get(suite.id);
        const isMobile = container ? container.isMobileExecution() : this.getSuiteExecutionTypeOptions(suite.id).includes('mobile');
        if (isMobile) {
          return true;
        }
      }
      return this.suiteHasFileUploadChanges(suite.id);
    });
  }

  private suiteHasFileUploadChanges(suiteId: string): boolean {
    const container = this.suiteExecutionContainerRefs.get(suiteId);
    
    const csvReset = !!(this.suiteCsvResetRequesteds.get(suiteId) || (container && container.scheduleCsvResetRequested));
    const apkReset = !!(this.suiteApkResetRequesteds.get(suiteId) || (container && container.scheduleApkResetRequested));

    if (csvReset || apkReset) {
      return true;
    }

    const hasCsv = this.suiteUploadedCsvs.has(suiteId) || (container && container.hasUploadedCsv);
    const hasApk = this.suiteUploadedApks.has(suiteId) || !!(container && container.apkFile);
    if (hasCsv || hasApk) {
      return true;
    }

    const snapshot = this.suiteFileSnapshots.get(suiteId);
    if (!snapshot) {
      const setCsv = !!(this.suiteSetCsvForSuites.get(suiteId) || (container && container.setCsvForSuite));
      const setApk = !!(this.suiteSetApkForSuites.get(suiteId) || (container && container.setApkForSuite));
      return setCsv || setApk;
    }

    const useDefaultCsv = this.suiteUseDefaultCsvs.has(suiteId) ? this.suiteUseDefaultCsvs.get(suiteId) : (container ? container.useDefaultCsv : false);
    const useDefaultApk = this.suiteUseDefaultApks.has(suiteId) ? this.suiteUseDefaultApks.get(suiteId) : (container ? container.useDefaultApk : false);
    const setCsv = this.suiteSetCsvForSuites.has(suiteId) ? this.suiteSetCsvForSuites.get(suiteId) : (container ? container.setCsvForSuite : false);
    const setApk = this.suiteSetApkForSuites.has(suiteId) ? this.suiteSetApkForSuites.get(suiteId) : (container ? container.setApkForSuite : false);
    const hadCsv = container ? !!container.scheduleAttachedCsvPath : false;
    const hadApk = container ? !!container.scheduleAttachedApkPath : false;

    return (
      !!useDefaultCsv !== snapshot.useDefaultCsv ||
      !!useDefaultApk !== snapshot.useDefaultApk ||
      hadCsv !== snapshot.hadScheduleCsv ||
      hadApk !== snapshot.hadScheduleApk ||
      !!setCsv !== snapshot.setCsvForSuite ||
      !!setApk !== snapshot.setApkForSuite
    );
  }
}
